import { duracaoAgendamentoRecord } from '@agenda/model-agenda'
import { Button, HFlow, Icon, InfoLabel, Modal, ModalBody, ModalFooter, ModalHeader, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useErrorHandler } from 'components/error'
import { CheckboxField, Form, FormRenderProps, NumberField, PecFormApi, SubmitButton } from 'components/form'
import { confirm } from 'components/modals/confirm'
import { Interpolation } from 'emotion'
import { useAlterarDuracaoCboMutation, useCboEditComponentQuery } from 'graphql/hooks.generated'
import { CboDuracaoFormInput } from 'graphql/types.generated'
import { useState } from 'react'
import { metaPath } from 'util/metaPath'
import { composeRules, createValidator, ErrorObject, range, required } from 'util/validation'

import { CboTableItemDto } from './CboTable'

const limitesDuracaoAgendamento = duracaoAgendamentoRecord.padrao

export interface CboEditComponentProps {
  item: CboTableItemDto
  style?: Interpolation

  reload(): Promise<any>
}

const validate = createValidator<CboEditModel>({}, (values: CboEditModel) => {
  const errors: ErrorObject<CboEditModel> = {}

  if (!values.usarDuracaoPadrao) {
    errors.duracao = composeRules([
      required,
      range(limitesDuracaoAgendamento.minimoEmMinutos, limitesDuracaoAgendamento.maximoEmMinutos),
    ])(values.duracao)
  }

  return errors
})

interface CboEditModel {
  id: ID
  duracao: number
  duracaoPadrao: number
  usarDuracaoPadrao: boolean
}

const path = metaPath<CboEditModel>()

export function CboEditComponent(props: Readonly<CboEditComponentProps>) {
  const { item, reload } = props

  const { data } = useCboEditComponentQuery()
  const possuiLotacaoComAgendaOnline = props.item.possuiLotacaoComAgendaOnline
  const [open, setOpen] = useState(false)
  const [alterarDuracaoCbo, { loading }] = useAlterarDuracaoCboMutation()
  const alert = useAlert()
  const handleRejection = useErrorHandler()

  const renderForm = (formProps: FormRenderProps) => {
    const { usarDuracaoPadrao } = formProps.values

    return (
      <Modal open={open} onClose={() => setOpen(false)} size='small'>
        <ModalHeader title='Editar CBO' />
        <ModalBody>
          <VFlow vSpacing={1} style={{ width: 300 }}>
            <InfoLabel title='Nome' placeholder='-'>
              {item.cbo.nome?.capitalize()}
            </InfoLabel>
            <InfoLabel title='Código' placeholder='-'>
              {item.cbo.cbo2002}
            </InfoLabel>
            <NumberField
              name={!usarDuracaoPadrao ? path.duracao.absolutePath() : path.duracaoPadrao.absolutePath()}
              label='Duração do agendamento (min)'
              required={!usarDuracaoPadrao}
              disabled={usarDuracaoPadrao}
              maxLength={2}
            />
            <CheckboxField name={path.usarDuracaoPadrao} label='Usar duração padrão do município' />
          </VFlow>
        </ModalBody>
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <Button onClick={handleCancel(formProps)} data-cy='CancelarButton'>
              Cancelar
            </Button>
            <SubmitButton kind='primary' handleSubmit={formProps.handleSubmit} data-cy='SubmitButton'>
              Salvar
            </SubmitButton>
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }

  const handleCancel = (formProps: FormRenderProps) => () => {
    formProps.form.reset()
    setOpen(false)
  }

  const handleSubmit = (values: CboEditModel, formApi: PecFormApi) => {
    const { usarDuracaoPadrao, duracaoPadrao, ...rest } = values
    const { duracao: oldDuracao } = formApi.getState().initialValues as CboEditModel
    const cboDuracaoForm: CboDuracaoFormInput = {
      ...rest,
      duracao: usarDuracaoPadrao ? null : +rest.duracao,
    }
    if (oldDuracao === cboDuracaoForm.duracao) {
      onEditSuccess(formApi)
      return
    }
    if (data.conexao.agendaOnline.ativa) {
      checkCBOAgendaOnline(cboDuracaoForm, formApi)
    } else {
      return doAlterarCbo(cboDuracaoForm, formApi)
    }
  }

  const checkCBOAgendaOnline = (cboDuracaoForm: CboDuracaoFormInput, formApi: PecFormApi) => {
    if (possuiLotacaoComAgendaOnline) {
      confirm({
        title: `Deseja confirmar a exclusão das configurações de agenda online?`,
        body:
          `Alterar a duração do agendamento pode causar inconsistências nas configurações ` +
          `de horários disponibilizados para agenda online. Todas as lotações que possuem esta ocupação ` +
          `terão sua configuração de agenda online excluída.`,
        confirmLabel: 'Sim',
        cancelLabel: 'Não',
        type: 'primary',
        onConfirm: () => {
          doAlterarCbo(cboDuracaoForm, formApi)
        },
      })()
    } else {
      doAlterarCbo(cboDuracaoForm, formApi)
    }
  }

  const onEditSuccess = (formApi: PecFormApi) => {
    alert('success', 'CBO editado com sucesso.')
    setTimeout(formApi.reset)
    setOpen(false)
    reload()
  }

  const doAlterarCbo = (cboDuracaoForm: CboDuracaoFormInput, formApi: PecFormApi) => {
    return (
      !loading &&
      alterarDuracaoCbo({ variables: { cboDuracaoForm } })
        .then(() => {
          onEditSuccess(formApi)
        })
        .catch(handleRejection)
    )
  }

  return (
    <>
      <Tooltip text='Editar'>
        <Button size='small' skin='ghost' onClick={() => setOpen(true)} style={props.style} data-cy='EditarButton'>
          <Icon icon='penOutline' />
        </Button>
      </Tooltip>
      <Form
        render={renderForm}
        onSubmit={handleSubmit}
        initialValues={
          {
            id: item.cbo.id,
            duracao: item.duracao,
            duracaoPadrao: item.duracaoPadrao,
            usarDuracaoPadrao: !item.duracao,
          } as CboEditModel
        }
        validate={validate}
        onSubmitFailed={null}
      />
    </>
  )
}
